import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, SignOut, Heart, TrendUp, Star } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { pressable } from '../lib/a11y';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FanDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  useEffect(() => {
    fetchMovies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMovies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/movies`);
      setMovies(response.data);
    } catch (error) {
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const filteredMovies = selectedIndustry === 'all' 
    ? movies 
    : movies.filter(m => m.industry === selectedIndustry);

  const stats = {
    totalMovies: movies.length,
    bollywood: movies.filter(m => m.industry === 'Bollywood').length,
    hollywood: movies.filter(m => m.industry === 'Hollywood').length,
    tollywood: movies.filter(m => m.industry === 'Tollywood').length,
    kollywood: movies.filter(m => m.industry === 'Kollywood').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      
      {/* Navbar */}
      <nav data-web-chrome="nav" className="relative z-10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} data-testid="nav-home-btn" className="flex items-center space-x-3">
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
            </button>
            <div className="flex items-center space-x-4">
              <button
                data-testid="nav-community-btn"
                onClick={() => navigate('/community')}
                className="btn-secondary"
              >
                Community
              </button>
              <button
                data-testid="nav-marketplace-btn"
                onClick={() => navigate('/marketplace')}
                className="btn-secondary"
              >
                Marketplace
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={handleLogout}
                className="btn-secondary flex items-center space-x-2"
              >
                <SignOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-5xl font-bold mb-2">
            FAN <span className="text-gold">DASHBOARD</span>
          </h1>
          <p className="font-body text-xl text-slate-400">Welcome back, {user.name} 🎬</p>
        </div>

        {/* Industry Stats */}
        <div data-testid="industry-stats" className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <FilmStrip size={32} className="text-gold mb-3" weight="bold" />
            <div className="font-body text-xs text-slate-400 mb-1">All Movies</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{stats.totalMovies}</div>
          </div>
          
          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <Star size={32} className="text-gold mb-3" weight="fill" />
            <div className="font-body text-xs text-slate-400 mb-1">Bollywood</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{stats.bollywood}</div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <Star size={32} className="text-gold mb-3" weight="fill" />
            <div className="font-body text-xs text-slate-400 mb-1">Hollywood</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{stats.hollywood}</div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <Star size={32} className="text-gold mb-3" weight="fill" />
            <div className="font-body text-xs text-slate-400 mb-1">Tollywood</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{stats.tollywood}</div>
          </div>

          <div className="bg-card border border-white/5 rounded-lg p-6 hover:border-white/10 transition-colors">
            <Star size={32} className="text-gold mb-3" weight="fill" />
            <div className="font-body text-xs text-slate-400 mb-1">Kollywood</div>
            <div className="font-mono text-2xl font-bold tabular-nums">{stats.kollywood}</div>
          </div>
        </div>

        {/* Industry Filter */}
        <div className="mb-8">
          <h2 className="font-heading text-3xl font-bold mb-6">EXPLORE BY INDUSTRY</h2>
          <div className="flex gap-3 flex-wrap">
            {['all', 'Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'].map((industry) => (
              <button
                key={industry}
                data-testid={`fan-filter-${industry.toLowerCase()}-btn`}
                onClick={() => setSelectedIndustry(industry)}
                className={`px-6 py-3 rounded-sm font-body text-sm uppercase tracking-wide transition-all ${
                  selectedIndustry === industry
                    ? 'bg-gold text-black font-bold'
                    : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
                }`}
              >
                {industry === 'all' ? 'All Industries' : industry}
              </button>
            ))}
          </div>
        </div>

        {/* Movies Grid */}
        <div>
          <h2 className="font-heading text-3xl font-bold mb-6">
            {selectedIndustry === 'all' ? 'ALL MOVIES' : selectedIndustry.toUpperCase() + ' MOVIES'}
          </h2>
          {filteredMovies.length === 0 ? (
            <div className="bg-card border border-white/5 rounded-lg p-12 text-center">
              <FilmStrip size={64} className="text-slate-400 mx-auto mb-4" />
              <p className="font-body text-xl text-slate-400 mb-6">No movies found</p>
              <button
                data-testid="browse-all-btn"
                onClick={() => navigate('/marketplace')}
                className="btn-primary"
              >
                Browse Marketplace
              </button>
            </div>
          ) : (
            <div data-testid="fan-movies-grid" className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredMovies.map((movie) => {
                const fundingPct = Math.min((movie.raised_amount / movie.target_amount) * 100, 100);
                return (
                  <motion.div
                    key={movie.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="movie-card aspect-[2/3]"
                    {...pressable(() => navigate(`/movie/${movie.id}`), `Open ${movie.title}`)}
                    data-testid={`fan-movie-card-${movie.id}`}
                  >
                    <div className="relative w-full h-full">
                      <img
                        src={movie.poster_url}
                        alt={movie.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block px-3 py-1 bg-gold text-black rounded-sm font-bold text-xs">
                          {movie.industry}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="mb-3">
                          <span className="inline-block px-3 py-1 bg-gold/20 border border-gold/50 rounded-sm text-gold text-xs font-bold uppercase tracking-wide">
                            {movie.genre}
                          </span>
                        </div>
                        <h3 className="font-heading text-xl font-bold mb-2">{movie.title}</h3>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="font-body text-xs text-slate-400 mb-1">Funded</div>
                            <div className="font-mono text-sm font-bold text-gold tabular-nums">
                              {fundingPct.toFixed(0)}%
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 text-gold">
                            <Heart size={20} weight="fill" />
                            <span className="font-body text-sm font-bold">Fan Favorite</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FanDashboard;
